// Single source of truth for "AI Inbox-only" lockdown.
//
// Reads the `ai-inbox-only` LaunchDarkly flag (org-level targeting in LD,
// resolved as `flags.aiInboxOnly` here per LD's default camelCase mapping)
// and exposes:
//   - `locked` — true when the current org is restricted to the inbox surface
//   - `isAllowedPath(pathname)` — predicate the AgrifulFrame guard uses to
//     decide whether to redirect away from the current route
//
// Centralizing the allow list here keeps the redirect guard, the sidebar
// short-circuit, and the header chrome gates from drifting. Extending the
// list (e.g. to permit `/settings/portal` later) means one edit here.

import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback } from 'react';
import { useAiV2Enabled } from 'src/hooks/useAiV2Enabled';
import { isChatHostPath } from 'src/routing/chatHostPaths';

// Path prefixes locked users may navigate to: the inbox, the review of a new document layout an inbox thread links
// to, and the profile. Match is by exact equality
// OR by `prefix + '/'` — so `/ai-inbox` matches itself and `/ai-inbox/42`,
// but NOT `/ai-inbox-archive` (no accidental sibling matches).
export const ALLOWED_PATH_PREFIXES = ['/ai-inbox', '/document-onboardings', '/user-profile'] as const;

export function isAllowedPath(pathname: string): boolean {
  return ALLOWED_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

interface AiInboxOnlyMode {
  locked: boolean;
  isAllowedPath: (pathname: string) => boolean;
}

export function useAiInboxOnlyMode(): AiInboxOnlyMode {
  const flags = useFlags();
  const aiV2 = useAiV2Enabled();
  const locked = Boolean(flags.aiInboxOnly);

  // Stable reference so consumers can pass this directly into useEffect deps
  // without churning the effect on every render.
  const check = useCallback(
    (pathname: string) => isAllowedPath(pathname) || (aiV2 && isChatHostPath(pathname)),
    [aiV2]
  );

  return { locked, isAllowedPath: check };
}
